// src/pages/Home.jsx

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, EffectCoverflow } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/effect-coverflow';
import {
  FaShoppingCart,
  FaArrowRight,
  FaSpinner,
  FaCheckCircle,
  FaTimes,
  FaBolt,
  FaTint,
  FaLeaf,
  FaStar,
  FaShippingFast,
  FaShieldAlt,
  FaInfinity,
  FaFlask,
  FaExclamationTriangle,
  FaFire,
  FaTag
} from 'react-icons/fa';

import '../styles/Home.css';
import reanimeitorImage from '../Assets/reanimeitor-can.png.png';
import packImage from '../Assets/pack24.png';
import ShippingForm from '../components/ShippingForm';

const Home = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [user, setUser] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [purchasedPack, setPurchasedPack] = useState(null);
  const [showShippingForm, setShowShippingForm] = useState(false);
  const [selectedPack, setSelectedPack] = useState(null);
  const [soldCans, setSoldCans] = useState(2855);

  const [discountCode, setDiscountCode] = useState('');
  const [discountApplied, setDiscountApplied] = useState(null);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [discountMessage, setDiscountMessage] = useState('');

  const navigate = useNavigate();

  const TRACKING_BASE = 'https://reanimeitor.com/backend/tracking';

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setSoldCans(prev => prev + Math.floor(Math.random() * 3));
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const loadUser = async () => {
    try {
      const res = await axios.get('https://reanimeitor.com/backend/profile.php', {
        withCredentials: true
      });

      if (res.data.success && res.data.user) {
        setUser(res.data.user);
        localStorage.setItem('userData', JSON.stringify(res.data.user));
      } else {
        setUser(null);
        localStorage.removeItem('userData');
      }
    } catch (err) {
      setUser(null);
    }
  };

  useEffect(() => {
    loadUser();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get('pack_success');
    const sessionId = params.get('session_id');

    if (success === 'true' && sessionId) {
      const verify = async () => {
        try {
          const res = await axios.post(
            'https://reanimeitor.com/backend/verify_payment.php',
            { session_id: sessionId },
            { withCredentials: true }
          );

          if (res.data.success && res.data.paid) {
            setPurchasedPack({ name: res.data.pack_name });
            setShowSuccessPopup(true);
            await loadUser();
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        } catch (err) {
          console.error(err);
        }
      };

      verify();
    }
  }, []);

  const packsData = [
    {
      id: 2,
      name: 'Pack 24',
      price: 22.99,
      pricePerCan: 0.96,
      description: '24 latas de energía real. El pack ideal para empezar.',
      image: packImage,
      features: [
        '⚡ 51 kcal/100ml',
        '☕ Cafeína 30mg/100ml',
        '🍬 Azúcares 11.5g/100ml',
        '🧬 Taurina + vitaminas B6 y B12'
      ],
      color: '#60a5fa',
      tag: 'MÁS VENDIDO'
    },
    {
      id: 3,
      name: 'Pack 48',
      price: 44.99,
      pricePerCan: 0.96,
      description: '48 latas. Mejor precio por lata para los que no paran.',
      image: packImage,
      features: [
        '⚡ 51 kcal/100ml',
        '☕ Cafeína 30mg/100ml',
        '🍬 Azúcares 11.5g/100ml',
        '🏆 Mejor precio',
        '🧬 Taurina + vitaminas B6 y B12'
      ],
      color: '#a855f7',
      tag: 'AHORRO MÁXIMO',
      bestValue: true
    }
  ];

  const calculateDiscountData = (pack) => {
    const originalPrice = Number(pack?.price || 0);
    const discountPercent = Number(discountApplied?.percent || 0);
    const hasDiscount = !!discountApplied?.code && discountPercent > 0;

    if (!hasDiscount) {
      return {
        originalPrice,
        finalPrice: originalPrice,
        discountAmount: 0,
        discountPercent: 0,
        hasDiscount: false,
        discountCode: ''
      };
    }

    const discountAmount = Number(((originalPrice * discountPercent) / 100).toFixed(2));
    const finalPrice = Number(Math.max(originalPrice - discountAmount, 0.5).toFixed(2));

    return {
      originalPrice,
      finalPrice,
      discountAmount,
      discountPercent,
      hasDiscount: true,
      discountCode: discountApplied.code
    };
  };

  const getDiscountedPrice = (price) => {
    if (!discountApplied || !discountApplied.percent) {
      return Number(price);
    }

    const discountPercent = Number(discountApplied.percent || 0);
    const originalPrice = Number(price || 0);
    const discountAmount = (originalPrice * discountPercent) / 100;
    const finalPrice = originalPrice - discountAmount;

    return Math.max(finalPrice, 0.5);
  };

  const buildCheckoutPack = (pack) => {
    const discountData = calculateDiscountData(pack);

    return {
      ...pack,
      price: discountData.finalPrice,
      original_price: discountData.originalPrice,
      originalPrice: discountData.originalPrice,
      final_price: discountData.finalPrice,
      finalPrice: discountData.finalPrice,
      amount_eur: discountData.finalPrice,
      has_discount: discountData.hasDiscount,
      hasDiscount: discountData.hasDiscount,
      discount_code: discountData.discountCode,
      discountCode: discountData.discountCode,
      discount_percent: discountData.discountPercent,
      discountPercent: discountData.discountPercent,
      discount_amount: discountData.discountAmount,
      discountAmount: discountData.discountAmount
    };
  };

  const validateDiscountCode = async () => {
    const code = String(discountCode || '').trim().toUpperCase();

    setDiscountMessage('');
    setDiscountApplied(null);

    if (!user) {
      setDiscountMessage('Debes iniciar sesión para usar un código de descuento.');
      return;
    }

    if (!user?.email || !user?.id) {
      setDiscountMessage('No se pudo identificar tu cuenta. Cierra sesión y vuelve a entrar.');
      return;
    }

    if (!code) {
      setDiscountMessage('Introduce un código de descuento.');
      return;
    }

    setDiscountLoading(true);

    try {
      const res = await axios.post(
        'https://reanimeitor.com/blog/wp-json/reanimeitor/v1/validar-descuento',
        {
          code,
          email: user.email,
          react_user_id: user.id
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (res.data?.success && res.data?.valid) {
        const percent = Number(res.data.discount_percent || 10);
        const normalizedCode = String(res.data.code || code).trim().toUpperCase();

        setDiscountApplied({
          code: normalizedCode,
          percent,
          promo_code: !!res.data.promo_code
        });

        setDiscountCode(normalizedCode);
        setDiscountMessage(`Descuento aplicado correctamente: ${percent}%`);
      } else {
        setDiscountMessage(res.data?.error || 'Código de descuento no válido.');
      }
    } catch (err) {
      const apiError = err?.response?.data?.error;
      setDiscountMessage(apiError || 'Código de descuento no válido.');
    } finally {
      setDiscountLoading(false);
    }
  };

  const clearDiscountCode = () => {
    setDiscountCode('');
    setDiscountApplied(null);
    setDiscountMessage('');
  };

  const updateCheckoutAttempt = async (customData = {}) => {
    const token =
      customData.attempt_token ||
      sessionStorage.getItem('reanimeitor_checkout_attempt_token') ||
      '';

    if (!token) {
      return;
    }

    try {
      await axios.post(
        `${TRACKING_BASE}/checkout_update.php`,
        {
          attempt_token: token,
          user_id: user?.id || null,
          current_url: window.location.href,
          ...customData
        },
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json'
          },
          timeout: 10000
        }
      );
    } catch (err) {
      console.warn('No se pudo actualizar el intento de compra:', err);
    }
  };

  const handleBuy = (pack) => {
    if (!user) {
      localStorage.setItem('pending_pack', JSON.stringify(pack));
      navigate('/login');
      return;
    }

    const checkoutPack = buildCheckoutPack(pack);

    setSelectedPack(checkoutPack);
    setShowShippingForm(true);
  };

  const handleShippingSuccess = async (shippingData) => {
    setShowShippingForm(false);
    setIsProcessing(true);

    const attemptToken =
      shippingData?.attempt_token ||
      sessionStorage.getItem('reanimeitor_checkout_attempt_token') ||
      '';

    try {
      await axios.post(
        'https://reanimeitor.com/backend/save_shipping_address.php',
        {
          user_id: user.id,
          full_name: shippingData.full_name,
          phone: shippingData.phone,
          address: shippingData.address,
          city: shippingData.city,
          postal_code: shippingData.postal_code,
          country: shippingData.country || 'España'
        },
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      await updateCheckoutAttempt({
        attempt_token: attemptToken,
        step: 'shipping_form_completed',
        status: 'shipping_form',
        customer_name: shippingData.full_name,
        customer_email: shippingData.email || user?.email || '',
        customer_phone: shippingData.phone,
        customer_address: shippingData.address,
        customer_city: shippingData.city,
        customer_postal_code: shippingData.postal_code,
        customer_country: shippingData.country || 'España',
        pack_id: selectedPack?.id || null,
        pack_name: selectedPack?.name || '',
        original_amount_eur: selectedPack?.original_price || selectedPack?.price || 0,
        amount_eur: selectedPack?.final_price || selectedPack?.price || 0,
        discount_code: selectedPack?.discount_code || '',
        discount_percent: selectedPack?.discount_percent || 0,
        discount_amount: selectedPack?.discount_amount || 0
      });

      const res = await axios.post(
        'https://reanimeitor.com/backend/create_stripe_session_pack.php',
        {
          pack_id: selectedPack.id,
          attempt_token: attemptToken,
          discount_code: selectedPack?.discount_code || '',
          discount_email: user?.email || '',
          react_user_id: user?.id || ''
        },
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (res.data.success && res.data.checkoutUrl) {
        await updateCheckoutAttempt({
          attempt_token: attemptToken,
          step: 'stripe_created',
          status: 'stripe_created',
          stripe_session_id: res.data.session_id || res.data.sessionId || '',
          original_amount_eur: selectedPack?.original_price || selectedPack?.price || 0,
          amount_eur: selectedPack?.final_price || selectedPack?.price || 0,
          discount_code: selectedPack?.discount_code || '',
          discount_percent: selectedPack?.discount_percent || 0,
          discount_amount: selectedPack?.discount_amount || 0
        });

        window.location.href = res.data.checkoutUrl;
      } else {
        alert(res.data.error || 'Error al crear sesión de pago');
      }
    } catch (err) {
      console.error(err);

      const apiError = err?.response?.data?.error;
      const stripeError = err?.response?.data?.stripe_error;

      alert(apiError || stripeError || 'Error de conexión');
    } finally {
      setIsProcessing(false);
    }
  };

  const scrollToPacks = () => {
    document.querySelector('#elige-tu-carga')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };

  return (
    <div className="home-futuristic">
      <div className="energy-background">
        <div className="particle-field"></div>
        <div className="grid-overlay"></div>
      </div>

      <section className="home-banner-section" aria-label="Reanimeitor Energy Drink">
        <div className="home-banner-fade home-banner-fade-top"></div>

        <img
          src="/Assets/Banner.jpeg"
          alt="Reanimeitor Energy Drink bebida energética oficial"
          className="home-banner-image"
          loading="eager"
          decoding="async"
          fetchPriority="high"
        />

        <div className="home-banner-desktop-content">
          <div className="home-banner-kicker">
            <FaBolt />
            <span>ENERGÍA REAL · PACKS DISPONIBLES</span>
          </div>

          <p>
            Reanimeitor Energy Drink está pensado para estudiar, entrenar,
            crear contenido, jugar o aguantar esos días en los que necesitas
            reactivarte de verdad.
          </p>

          <button
            type="button"
            className="home-banner-buy-btn"
            onClick={scrollToPacks}
          >
            Comprar ahora <FaArrowRight />
          </button>

          <div className="home-banner-minirow">
            <span>Pack 24</span>
            <span>Pack 48</span>
            <span>Envío 24-48h</span>
          </div>
        </div>

        <div className="home-banner-fade home-banner-fade-bottom"></div>
      </section>

      {showShippingForm && selectedPack && (
        <ShippingForm
          pack={{
            ...selectedPack,
            price: Number(selectedPack.final_price || selectedPack.price || 0),
            original_price: Number(selectedPack.original_price || selectedPack.price || 0),
            originalPrice: Number(selectedPack.original_price || selectedPack.price || 0),
            final_price: Number(selectedPack.final_price || selectedPack.price || 0),
            finalPrice: Number(selectedPack.final_price || selectedPack.price || 0),
            amount_eur: Number(selectedPack.final_price || selectedPack.price || 0),
            has_discount: !!selectedPack.has_discount,
            hasDiscount: !!selectedPack.has_discount,
            discount_code: selectedPack.discount_code || '',
            discountCode: selectedPack.discount_code || '',
            discount_percent: Number(selectedPack.discount_percent || 0),
            discountPercent: Number(selectedPack.discount_percent || 0),
            discount_amount: Number(selectedPack.discount_amount || 0),
            discountAmount: Number(selectedPack.discount_amount || 0)
          }}
          user={user}
          onSuccess={handleShippingSuccess}
          onCancel={() => setShowShippingForm(false)}
        />
      )}

      {showSuccessPopup && purchasedPack && (
        <div className="success-popup-overlay">
          <div className="success-popup">
            <button
              className="popup-close-btn"
              onClick={() => setShowSuccessPopup(false)}
            >
              <FaTimes />
            </button>

            <div className="popup-icon">
              <FaCheckCircle />
            </div>

            <h3>¡PEDIDO CONFIRMADO!</h3>

            <p className="popup-pack-name">
              <strong>{purchasedPack.name}</strong>
            </p>

            <p>Prepárate para la energía. Llega en 24-48h.</p>

            <div className="popup-actions">
              <button
                className="popup-btn primary"
                onClick={() => {
                  setShowSuccessPopup(false);
                  navigate('/account?tab=orders');
                }}
              >
                Ver pedido
              </button>

              <button
                className="popup-btn secondary"
                onClick={() => setShowSuccessPopup(false)}
              >
                Seguir comprando
              </button>
            </div>
          </div>
        </div>
      )}

      <section className="hero-futuristic">
        <div className="hero-container">
          <div className="hero-main">
            <div className="hero-left">
              <h1 className="futuristic-title">
                <span className="title-sub">ENERGÍA QUE TE TRASCIENDE</span>
              </h1>

              <div className="hero-stats">
                <div className="stat-item">
                  <FaInfinity className="stat-icon" />
                  <span>{soldCans.toLocaleString()}</span>
                  <small>latas vendidas</small>
                </div>

                <div className="stat-divider"></div>

                <div className="stat-item">
                  <FaStar className="stat-icon" />
                  <span>4.98</span>
                  <small>★★★★★</small>
                </div>
              </div>

              <p className="hero-description-future">
                51 kcal/100ml · Cafeína 30mg/100ml · Azúcares 11.5g/100ml
                <br />
                La fórmula perfecta para cuando necesitas reactivarte.
              </p>

              <div className="hero-benefits-future">
                <div className="benefit-pill">
                  <FaBolt />
                  <span>Taurina</span>
                </div>

                <div className="benefit-pill">
                  <FaTint />
                  <span>30mg cafeína</span>
                </div>

                <div className="benefit-pill">
                  <FaFire />
                  <span>51 kcal</span>
                </div>

                <div className="benefit-pill">
                  <FaFlask />
                  <span>B6 · B12 · Niacina</span>
                </div>
              </div>

              <div className="formula-panel-future">
                <div className="formula-panel-header">
                  <FaFlask />
                  <span>FÓRMULA REANIMEITOR</span>
                </div>

                <div className="formula-grid">
                  <div>
                    <strong>30mg</strong>
                    <small>cafeína / 100ml</small>
                  </div>

                  <div>
                    <strong>B6</strong>
                    <small>143% VRN</small>
                  </div>

                  <div>
                    <strong>B12</strong>
                    <small>80% VRN</small>
                  </div>

                  <div>
                    <strong>B3</strong>
                    <small>= Niacina</small>
                  </div>
                </div>

                <p className="formula-warning">
                  <FaExclamationTriangle />
                  Contenido elevado de cafeína. No recomendado para niños,
                  embarazadas o periodo de lactancia.
                </p>
              </div>

              <button className="hero-cta-future" onClick={scrollToPacks}>
                ACTIVAR ENERGÍA <FaArrowRight className="cta-icon-future" />
              </button>
            </div>

            <div className="hero-right">
              <div className="can-3d-container">
                <div className="can-glow-ring"></div>

                <div className="can-rotator">
                  <img
                    src={reanimeitorImage}
                    alt="reanimeitor"
                    className="can-3d"
                  />
                </div>

                <div className="energy-particles">
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="scroll-indicator" onClick={scrollToPacks}>
          <span></span>
          <span></span>
          <span></span>
        </div>
      </section>

      <section id="elige-tu-carga" className="packs-futuristic">
        <div className="section-header-future">
          <h2>
            ELIGE TU <span className="highlight">CARGA</span>
          </h2>
          <div className="header-beam"></div>
        </div>

        <div className="discount-code-box">
          <div className="discount-code-title">
            <FaTag />
            <h3>Código de descuento</h3>
          </div>

          <p>
            Si has ganado un descuento en el Rasca y Gana o tienes un código promocional, introdúcelo antes de comprar.
          </p>

          <div className="discount-code-row">
            <input
              type="text"
              value={discountCode}
              onChange={(e) => {
                setDiscountCode(e.target.value.toUpperCase());
                setDiscountApplied(null);
                setDiscountMessage('');
              }}
              placeholder="Introduce tu código"
              disabled={discountLoading || isProcessing}
            />

            <button
              type="button"
              onClick={validateDiscountCode}
              disabled={discountLoading || isProcessing}
            >
              {discountLoading ? 'Validando...' : 'Aplicar'}
            </button>

            {(discountCode || discountApplied) && (
              <button
                type="button"
                className="discount-clear-btn"
                onClick={clearDiscountCode}
                disabled={discountLoading || isProcessing}
              >
                Quitar
              </button>
            )}
          </div>

          {discountMessage && (
            <p className={discountApplied ? 'discount-code-ok' : 'discount-code-error'}>
              {discountMessage}
            </p>
          )}
        </div>

        {isMobile ? (
          <Swiper
            modules={[Pagination, EffectCoverflow]}
            effect="coverflow"
            grabCursor
            centeredSlides
            slidesPerView="auto"
            coverflowEffect={{
              rotate: 30,
              stretch: 0,
              depth: 100,
              modifier: 1,
              slideShadows: true
            }}
            pagination={{ clickable: true }}
            className="packs-swiper-future"
          >
            {packsData.map(pack => (
              <SwiperSlide key={pack.id}>
                <FuturisticCard
                  pack={pack}
                  user={user}
                  onBuy={handleBuy}
                  isProcessing={isProcessing}
                  discountApplied={discountApplied}
                  discountedPrice={getDiscountedPrice(pack.price)}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          <div className="packs-grid-future">
            {packsData.map(pack => (
              <FuturisticCard
                key={pack.id}
                pack={pack}
                user={user}
                onBuy={handleBuy}
                isProcessing={isProcessing}
                discountApplied={discountApplied}
                discountedPrice={getDiscountedPrice(pack.price)}
              />
            ))}
          </div>
        )}
      </section>

      <section className="features-futuristic">
        <div className="features-grid">
          <div className="feature-future">
            <FaBolt />
            <h4>ENERGÍA CONTROLADA</h4>
            <p>51 kcal por 100ml</p>
          </div>

          <div className="feature-future">
            <FaTint />
            <h4>CAFEÍNA ÓPTIMA</h4>
            <p>30mg/100ml · Efecto sostenido</p>
          </div>

          <div className="feature-future">
            <FaLeaf />
            <h4>FÓRMULA COMPLETA</h4>
            <p>Taurina, niacina, B6 y B12</p>
          </div>

          <div className="feature-future">
            <FaShippingFast />
            <h4>ENVÍO RÁPIDO</h4>
            <p>24-48h a tu casa</p>
          </div>
        </div>
      </section>
    </div>
  );
};

const FuturisticCard = ({
  pack,
  user,
  onBuy,
  isProcessing,
  discountApplied,
  discountedPrice
}) => {
  const cardRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;

    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;

    setTilt({
      x: x * 15,
      y: y * 15
    });
  };

  const handleMouseLeave = () => {
    setTilt({ x: 0, y: 0 });
  };

  const finalPrice = Number(discountedPrice || pack.price);
  const hasDiscount = !!discountApplied && Number(discountApplied.percent || 0) > 0;

  return (
    <div
      className="pack-card-future"
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        transform: `perspective(1000px) rotateX(${tilt.y}deg) rotateY(${tilt.x}deg)`
      }}
    >
      {pack.tag && <div className="pack-tag">{pack.tag}</div>}

      {hasDiscount && (
        <div className="pack-discount-tag">
          -{discountApplied.percent}% aplicado
        </div>
      )}

      <div className="pack-image-future">
        <img src={pack.image} alt={pack.name} />
      </div>

      <h3>{pack.name}</h3>

      <p className="pack-desc-future">{pack.description}</p>

      <ul className="pack-features-future">
        {pack.features.map((feature, index) => (
          <li key={index}>{feature}</li>
        ))}
      </ul>

      <div className="pack-price-future">
        {hasDiscount ? (
          <>
            <span className="price-old">€{Number(pack.price).toFixed(2)}</span>
            <span className="price-big">€{finalPrice.toFixed(2)}</span>
          </>
        ) : (
          <span className="price-big">€{Number(pack.price).toFixed(2)}</span>
        )}

        <span className="price-per">(€{pack.pricePerCan}/lata) Envío + IVA incluido</span>
      </div>

      <button
        className="buy-button-future"
        style={{ background: pack.color }}
        onClick={() => onBuy(pack)}
        disabled={isProcessing}
      >
        {isProcessing ? (
          <>
            <FaSpinner className="spin" /> PROCESANDO
          </>
        ) : (
          <>
            <FaShoppingCart /> COMPRAR AHORA
          </>
        )}
      </button>

      {!user && (
        <p className="login-warning-future">
          <FaShieldAlt /> Inicia sesión para comprar
        </p>
      )}
    </div>
  );
};

export default Home;